import { ArchiveIcon } from "lucide-react";
import type { MouseEvent, PointerEvent, RefCallback } from "react";

import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

interface SidebarThreadArchiveControlBaseProps {
  readonly threadId: string;
  readonly title: string;
  readonly onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
  /**
   * Where the control sits in its row.
   *
   * `"row"` (default) overlays a compact project row's right edge. `"inline"`
   * owns the Activity card reveal wrapper. `"cluster"` renders only the button
   * because a parent action group already owns the reveal behavior.
   */
  readonly placement?: "row" | "inline" | "cluster";
  readonly actionLabel?: string;
  readonly confirmLabel?: string;
}

interface SidebarThreadArchiveConfirmProps extends SidebarThreadArchiveControlBaseProps {
  readonly state: "confirming";
  readonly confirmButtonRef: RefCallback<HTMLButtonElement>;
  readonly onConfirm: (event: MouseEvent<HTMLButtonElement>) => void;
}

interface SidebarThreadArchiveAvailableProps extends SidebarThreadArchiveControlBaseProps {
  readonly state: "available";
  readonly confirmBeforeArchive: boolean;
  readonly onStartConfirmation: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly onArchiveImmediate: (event: MouseEvent<HTMLButtonElement>) => void;
}

export type SidebarThreadArchiveControlProps =
  | SidebarThreadArchiveConfirmProps
  | SidebarThreadArchiveAvailableProps;

/**
 * Sized from `--acard` where one exists, and from `1rem` where it does not.
 *
 * The Activity card declares `--acard` (t3code's 16px base) so its glyph lands
 * on t3code's 14px; the compact row declares nothing, so the fallback resolves
 * these to `1.25rem` and `0.875rem` — exactly the `size-5` / `size-3.5` the row
 * has always rendered. One control, two contexts, no branch.
 */
const ARCHIVE_BUTTON_CLASS =
  "inline-flex size-[calc(var(--acard,1rem)*1.25)] cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring";
const ARCHIVE_ICON_CLASS = "size-[calc(var(--acard,1rem)*0.875)]";
/**
 * `has-[:focus-visible]`, scoped to this wrapper — not the row's
 * `group-focus-within`.
 *
 * `SidebarThreadRow` renders its root through `<div role="button" tabIndex={0} />`,
 * so a click (left or right) focuses the row and satisfies focus-within across
 * the whole `<li>`. The control then pinned itself open and stayed there once
 * the pointer moved away, with the meta it displaces left blank. Scoping to the
 * wrapper keeps the keyboard path — tabbing onto the button still reveals it —
 * without letting a mouse click latch it. Upstream keys the same reveal the
 * same way, and `SidebarThreadSettleControl` already does on the Activity card.
 *
 * The label this control displaces reads the other half of the same rule:
 * `useSidebarThreadRow`'s `threadMetaClassName` hides on
 * `group-has-[:focus-visible]/sidebar-status-slot`, scoped to the
 * `group/sidebar-status-slot` div that wraps both of them in
 * `SidebarThreadRow`'s trailing cluster. Both ends must move together — a
 * hidden label with no control in its place is an empty slot.
 */
const ARCHIVE_REVEAL_CLASS =
  "pointer-events-none opacity-0 transition-opacity duration-150 group-hover/menu-sub-item:pointer-events-auto group-hover/menu-sub-item:opacity-100 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:opacity-100";
const ARCHIVE_BUTTON_WRAPPER_CLASS = `absolute top-1/2 right-1 -translate-y-1/2 ${ARCHIVE_REVEAL_CLASS}`;
/**
 * Absolute at rest, in flow only once the row is hovered or the button takes
 * keyboard focus.
 *
 * `opacity-0` hides the button but an `inline-flex` wrapper still occupies its
 * full width in the status slot, which pushed the resting label 20px inboard of
 * the card's right edge — measurably not where t3code puts it. Upstream parks
 * this cluster at `absolute inset-y-0 right-0` and only promotes it to `static`
 * on hover, so at rest the slot is exactly as wide as the label it holds. The
 * `items-stretch` slot gives the absolute box the line's full height, so the
 * glyph stays vertically centred without a translate.
 */
const ARCHIVE_BUTTON_INLINE_WRAPPER_CLASS = `pointer-events-none absolute inset-y-0 right-0 inline-flex shrink-0 items-center group-hover/menu-sub-item:static has-[:focus-visible]:static ${ARCHIVE_REVEAL_CLASS}`;

export function SidebarThreadArchiveControl(props: SidebarThreadArchiveControlProps) {
  if (props.state === "confirming") {
    return (
      <button
        ref={props.confirmButtonRef}
        type="button"
        data-thread-selection-safe
        data-testid={`thread-archive-confirm-${props.threadId}`}
        aria-label={`${props.confirmLabel ?? "Confirm archive"} ${props.title}`}
        className="inline-flex h-[calc(var(--acard,1rem)*1.25)] shrink-0 cursor-pointer items-center rounded-full bg-destructive-surface px-[calc(var(--acard,1rem)*0.5)] text-[10px] font-medium text-destructive ring-1 ring-inset ring-destructive-border transition-colors hover:bg-destructive/25 hover:ring-destructive-border focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-destructive/50"
        onPointerDown={props.onPointerDown}
        onClick={props.onConfirm}
      >
        {props.confirmLabel ?? "Confirm"}
      </button>
    );
  }

  const archiveButton = (
    <button
      type="button"
      data-thread-selection-safe
      data-testid={`thread-archive-${props.threadId}`}
      aria-label={`${props.actionLabel ?? "Archive"} ${props.title}`}
      className={ARCHIVE_BUTTON_CLASS}
      onPointerDown={props.onPointerDown}
      onClick={props.confirmBeforeArchive ? props.onStartConfirmation : props.onArchiveImmediate}
    >
      <ArchiveIcon className={ARCHIVE_ICON_CLASS} />
    </button>
  );

  if (props.placement === "cluster") {
    return props.confirmBeforeArchive ? (
      archiveButton
    ) : (
      <Tooltip>
        <TooltipTrigger render={archiveButton} />
        <TooltipPopup side="top">{props.actionLabel ?? "Archive"}</TooltipPopup>
      </Tooltip>
    );
  }

  const wrapperClassName =
    props.placement === "inline"
      ? ARCHIVE_BUTTON_INLINE_WRAPPER_CLASS
      : ARCHIVE_BUTTON_WRAPPER_CLASS;

  return props.confirmBeforeArchive ? (
    <div className={wrapperClassName}>{archiveButton}</div>
  ) : (
    <Tooltip>
      <TooltipTrigger render={<div className={wrapperClassName}>{archiveButton}</div>} />
      <TooltipPopup side="top">{props.actionLabel ?? "Archive"}</TooltipPopup>
    </Tooltip>
  );
}
